import { Common } from 'src/domains/common/Common';
import { LocalStorageItemState } from 'src/domains/layouts/state/localStorage/LocalStorageItemState';
import { BetGliderLaunchUrlCacheEntryType } from 'src/domains/layouts/state/localStorage/localStorageItems/localStorageBetGliderLaunchUrl';
import { LocalStorageState } from 'src/domains/layouts/state/localStorage/LocalStorageState';
import { getBetGliderLaunchUrlExpiry } from 'src/domains/sportsbook/state/betGliderState/getBetGliderLaunchUrlExpiry';
import { AutoWeakMap } from 'src_common/common/mobx-utils/AutoWeakMap';
import { DateTime } from 'src_common/utils/time/time';

const isEntryValid = (entry: BetGliderLaunchUrlCacheEntryType): boolean => {
    return entry.expiresAt > DateTime.current().unixSeconds();
};

const matchesRequest = (
    entry: BetGliderLaunchUrlCacheEntryType,
    universe: string,
    userId: number,
    eventId: number
): boolean => {
    return entry.userId === userId && entry.eventId === eventId && entry.universe === universe;
};

export class BetGliderLaunchUrlCache {
    public static get = AutoWeakMap.create(
        (common: Common) => new BetGliderLaunchUrlCache(LocalStorageState.get(common).betGliderLaunchUrl)
    );

    public constructor(private readonly storage: LocalStorageItemState<BetGliderLaunchUrlCacheEntryType | null>) {}

    public get(universe: string, userId: number, eventId: number): string | null {
        const entry = this.storage.getValue();

        if (entry === null) {
            return null;
        }

        if (!isEntryValid(entry)) {
            this.storage.setValue(null);
            return null;
        }

        if (!matchesRequest(entry, universe, userId, eventId)) {
            return null;
        }

        return entry.url;
    }

    public set(universe: string, userId: number, eventId: number, url: string): void {
        const expiresAt = getBetGliderLaunchUrlExpiry(url);

        if (expiresAt === null) {
            console.warn('Bet glider launch URL cache: could not read ott exp from launch URL');
            return;
        }

        if (expiresAt <= DateTime.current().unixSeconds()) {
            return;
        }

        this.storage.setValue({
            url,
            expiresAt,
            userId,
            eventId,
            universe,
        });
    }

    public remove(universe: string, userId: number, eventId: number): void {
        const entry = this.storage.getValue();

        if (entry === null) {
            return;
        }

        if (matchesRequest(entry, universe, userId, eventId)) {
            this.storage.setValue(null);
        }
    }

    public pruneExpired(): void {
        const entry = this.storage.getValue();

        if (entry === null) {
            return;
        }

        if (!isEntryValid(entry)) {
            this.storage.setValue(null);
        }
    }
}
